/**
 * Le contrat du module **Clients**, côté navigateur (`B-05`, `EX-BO-004`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `CustomersMessages`, sans rien y ajouter.
 * `customers-maquette.test.tsx` confronte champ par champ cette déclaration au
 * **fichier Java lui-même** : recopier un contrat, c'est en faire une seconde
 * source de vérité qui vieillira (`D-35`, `D-45`).
 *
 * ## Deux routes, une seule forme de fiche
 *
 * `GET /api/backoffice/customers?…` rend une **page** ; `GET
 * /api/backoffice/customers/{id}?reason=` rend **une** fiche. La ligne de la
 * page et la fiche ouverte sont le **même** type, servi par le même dépôt et
 * la même requête à une clause près. C'est délibéré, et c'est ce qui évite
 * d'afficher le code d'un état dans le panneau — `EN_ATTENTE_KYC` — et son
 * libellé dans la table — « En attente de vérification » —, sur le même écran
 * et au même instant.
 *
 * On pourrait donc croire le second appel superflu — la ligne trouvée porte
 * déjà tout. **Il ne l'est pas, et c'est tout le sujet de cet écran.** La
 * maquette écrit « Toute consultation de fiche est journalisée (`EX-BO-004`) ».
 * Le serveur tient cette promesse *à l'appel* : la liste inscrit au journal
 * combien d'états civils elle a révélés, l'ouverture inscrit **lequel**. Un
 * écran qui dépilerait la ligne déjà reçue afficherait une fiche que le journal
 * ne mentionne pas — la promesse serait affichée et non tenue, ce qui est pire
 * que de ne pas la faire.
 *
 * ## Le motif est obligatoire sur les deux
 *
 * `BackOfficeService.requireReason` refuse un motif vide, et le journal le
 * conserve : c'est lui qui dit *pourquoi* la consultation a eu lieu.
 *
 * ## Le terme, lui, ne l'est plus — et c'est une inversion assumée
 *
 * La route précédente refusait un terme vide. L'écran s'ouvrait donc sur une
 * carte d'attente, et un agent qui ne savait pas quoi taper ne voyait rien. Ce
 * qui remplace ce frein est écrit côté serveur (`CustomersApi`) et inscrit au
 * registre : une fenêtre d'ouverture par défaut, une borne de page en
 * configuration, et une entrée d'audit qui compte les états civils révélés.
 *
 * ## Rien n'est calculé ici
 *
 * Le total, les quatre indicateurs, le compte par état, par niveau et par mois
 * viennent tous du serveur. Un chiffre calculé dans le navigateur est un
 * chiffre que le journal d'audit ne peut pas justifier.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin, tel que `CustomersApi` le sert. */
export const customersPath = '/api/backoffice/customers';

/**
 * La valeur qui demande explicitement qu'aucune fenêtre d'ouverture ne
 * s'applique — `CustomersApi.TOUTES_LES_PERIODES`.
 *
 * Elle n'est pas la chaîne vide : celle-ci veut dire « je n'ai rien demandé »,
 * et le serveur applique alors le mois courant. Les deux se distinguent, parce
 * qu'ils ne disent pas la même chose.
 */
export const TOUTES_LES_PERIODES = 'all';

/**
 * Une fiche client, exactement telle que le serveur la rend.
 *
 * `status` reste un **code** — `ACTIF`, `LIMITE`, `GELE`, `EN_ATTENTE_KYC` —,
 * et il arrive accompagné : `statusLabel` est le libellé que
 * `identity.customer_status` porte en base depuis la V9, `statusTone` la nature
 * que le serveur déduit de ce que cet état autorise. **Ni l'un ni l'autre n'est
 * écrit ici** : une table de correspondance dans le navigateur serait la liste
 * blanche partagée de `G3`, et le premier état ajouté en base la rendrait
 * fausse en silence.
 *
 * Six champs peuvent manquer, et chacun pour une raison qui se lit :
 * `firstName` et `lastName` sont absents d'un compte ouvert avant tout dépôt
 * d'identité — **quarante-huit fiches sur soixante et une** sur la base de
 * développement ; `balance` et `currency` sont absents **ensemble** quand le
 * client n'a pas de compte au grand livre, ce qui ne veut pas dire zéro ;
 * `lastActivityAt` est absent d'un compte qui n'a jamais servi ;
 * `outboundFrozenUntil` n'est porté que par un compte dont le numéro vient de
 * changer (`EX-SEC-007`) — ce **n'est pas** un gel administratif, et il n'entre
 * dans aucun compte de « gelés ».
 *
 * `balance` est en **unités mineures**, non formaté : la mise en forme
 * appartient à l'interface, qui connaît la langue de l'agent.
 */
export interface Customer {
  readonly id: string;
  readonly publicRef: string;
  readonly msisdn: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly kycLevel: number;
  readonly kycLabel: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly statusTone: string;
  readonly screeningStatus: string;
  readonly balance: number | null;
  readonly currency: string | null;
  readonly lastActivityAt: string | null;
  readonly createdAt: string;
  readonly outboundFrozenUntil: string | null;
  readonly pep: boolean;
}

/** Un état présent, son libellé servi, sa nature, et combien de fiches. */
export interface StatusFacet {
  readonly code: string;
  readonly label: string;
  readonly tone: string;
  readonly count: number;
}

/** Un niveau KYC présent, son libellé servi, et combien de fiches. */
export interface LevelFacet {
  readonly level: number;
  readonly label: string;
  readonly count: number;
}

/** Un mois d'ouverture présent — `AAAA-MM`, en UTC — et combien de fiches. */
export interface MonthFacet {
  readonly month: string;
  readonly count: number;
}

/**
 * Une page de la liste, avec ses facettes et ses quatre indicateurs.
 *
 * `openedFrom`, `openedTo` et `month` sont les bornes **effectivement
 * appliquées**, et les trois sont nulles ensemble quand l'écran a demandé
 * toutes les périodes. Nulles, elles disent « aucune fenêtre » — un fait
 * différent d'une fenêtre vide.
 *
 * `activeWithinDays` accompagne `activeRecently` pour que la carte nomme la
 * fenêtre qu'elle montre : le jour où elle passe de trente à soixante, l'écran
 * le dira sans qu'une ligne change ici.
 */
export interface CustomerPage {
  readonly customers: readonly Customer[];
  readonly total: number;
  readonly activeRecently: number;
  readonly frozen: number;
  readonly pendingKyc: number;
  readonly activeWithinDays: number;
  readonly page: number;
  readonly pageSize: number;
  readonly openedFrom: string | null;
  readonly openedTo: string | null;
  readonly month: string | null;
  readonly statuses: readonly StatusFacet[];
  readonly levels: readonly LevelFacet[];
  readonly months: readonly MonthFacet[];
}

/** Ce qu'une recherche porte. Seul le motif est obligatoire. */
export interface CustomerSearchQuery {
  /** Le mois d'ouverture, `AAAA-MM`, ou `TOUTES_LES_PERIODES`. */
  readonly month?: string;
  /** Le niveau KYC exact. */
  readonly kycLevel?: string;
  /** L'état exact, tel que le serveur le nomme. */
  readonly status?: string;
  /** Un nom, un numéro, ou la référence publique du compte. */
  readonly search?: string;
  /** Le rang de la page, à partir de 1. */
  readonly page?: number;
  /** Pourquoi ces fiches sont consultées. Le serveur la journalise avec. */
  readonly reason: string;
}

/**
 * Cherche des fiches.
 *
 * Aucune capture d'erreur : l'`ApiError` du client porte déjà sa nature et sa
 * clé de message. L'avaler pour rendre une page vide ferait confondre « aucun
 * client ne correspond » — un fait — avec « le serveur a refusé », qui n'en est
 * pas un. Sur un écran de recherche, la confusion se lirait comme l'absence du
 * client, c'est-à-dire l'inverse de ce qui s'est passé.
 *
 * Un critère absent ou vide n'est **pas** écrit dans l'adresse : `?status=`
 * serait un critère que le serveur devrait interpréter.
 */
export async function searchCustomers(
  client: ApiClient,
  query: CustomerSearchQuery,
): Promise<CustomerPage> {
  const parametres = new URLSearchParams({ reason: query.reason });
  const poser = (nom: string, valeur: string | undefined): void => {
    if (valeur !== undefined && valeur !== '') parametres.set(nom, valeur);
  };
  poser('month', query.month);
  poser('kycLevel', query.kycLevel);
  poser('status', query.status);
  poser('search', query.search);
  poser('page', query.page === undefined ? undefined : String(query.page));
  return client.request<CustomerPage>(`${customersPath}?${parametres.toString()}`);
}

/**
 * Ouvre une fiche — **et laisse la trace qui nomme ce client**.
 *
 * C'est l'appel qui tient la promesse de la maquette. Le voir dans le code du
 * navigateur ne prouve rien ; ce qui le prouve est
 * `RechercheDeClientsTest.ouvrir_une_fiche_trouvee_est_journalise`, qui relit
 * `admin.audit_log` après l'appel.
 */
export async function fetchCustomer(
  client: ApiClient,
  id: string,
  reason: string,
): Promise<Customer> {
  const parametres = new URLSearchParams({ reason });
  return client.request<Customer>(
    `${customersPath}/${encodeURIComponent(id)}?${parametres.toString()}`,
  );
}
